<template>
  <q-page class="landing">
    <KdsNav
      :logo-label="t('app.name')"
      :links="navLinks"
      :cta-primary="t('landing.nav.ctaPrimary')"
      :cta-secondary="t('landing.nav.ctaSecondary')"
      @cta-primary-click="goAuth"
      @cta-secondary-click="goSignIn"
    />

    <!-- Hero -->
    <section class="landing__hero">
      <div class="landing__hero-grid">
        <div class="landing__hero-text">
          <KdsEyebrow class="landing__hero-eyebrow">
            <span class="landing__hero-dot" />
            {{ t('landing.hero.eyebrow') }}
          </KdsEyebrow>
          <h1 class="landing__hero-title">
            {{ t('landing.hero.titlePrefix') }}
            <span class="landing__hero-highlight">{{ t('landing.hero.titleHighlight') }}</span>
          </h1>
          <p class="landing__hero-sub">{{ t('landing.hero.subtitle') }}</p>
          <div class="landing__hero-ctas">
            <KdsButton variant="primary" @click="goAuth">
              {{ t('landing.hero.ctaPrimary') }}
            </KdsButton>
            <KdsButton variant="ghost" icon="book-open" @click="goDocs">
              {{ t('landing.hero.ctaSecondary') }}
            </KdsButton>
          </div>
        </div>

        <!-- SSR placeholder — prevents CLS by reserving the hero visual space -->
        <div v-if="!isClientMounted" class="landing__hero-visual-placeholder" aria-hidden="true" />
        <div
          v-else
          class="landing__hero-visual"
          role="img"
          :aria-label="t('landing.hero.visualAriaLabel')"
        >
          <KdsRoundTable
            ref="roundTableRef"
            :size="480"
            :speaking-index="0"
            @speaker-change="(p) => pushSpeaker(p.index)"
          />
          <div class="landing__hero-live">
            <KdsLiveIndicator :turn="3" :total="4" />
          </div>
          <div class="landing__hero-quote">
            <KdsQuoteFeed :cards="feedCards" :paused="roundTablePaused" />
          </div>
        </div>
      </div>
    </section>

    <!-- How it works -->
    <section id="how" class="landing__section landing__how">
      <header class="landing__section-header">
        <KdsEyebrow>{{ t('landing.howItWorks.eyebrow') }}</KdsEyebrow>
        <h2 class="landing__section-title">{{ t('landing.howItWorks.title') }}</h2>
      </header>
      <div class="landing__steps">
        <KdsStepCard
          index="01"
          :tag="t('landing.howItWorks.step1Tag')"
          :title="t('landing.howItWorks.step1Title')"
          :body="t('landing.howItWorks.step1Body')"
        />
        <KdsStepCard
          index="02"
          :tag="t('landing.howItWorks.step2Tag')"
          :title="t('landing.howItWorks.step2Title')"
          :body="t('landing.howItWorks.step2Body')"
        />
        <KdsStepCard
          index="03"
          :tag="t('landing.howItWorks.step3Tag')"
          :title="t('landing.howItWorks.step3Title')"
          :body="t('landing.howItWorks.step3Body')"
        />
      </div>
    </section>

    <!-- Protocols -->
    <section id="protocols" class="landing__section landing__protocols landing__section--paper2">
      <header class="landing__section-header">
        <KdsEyebrow>{{ t('landing.protocols.eyebrow') }}</KdsEyebrow>
        <h2 class="landing__section-title">{{ t('landing.protocols.title') }}</h2>
        <p class="landing__section-subtitle">{{ t('landing.protocols.subtitle') }}</p>
      </header>
      <div class="landing__protocols-grid">
        <KdsProtocolCard
          :eyebrow="t('landing.protocols.devilsMeta')"
          :title="t('landing.protocols.devilsTitle')"
          :body="t('landing.protocols.devilsBody')"
          :best-for="t('landing.protocols.devilsBestFor')"
        >
          <template #visual>
            <KdsProtocolLoop
              kind="p01"
              :turns="4"
              :verdict-label="t('landing.protocols.devilsVerdict')"
            />
          </template>
        </KdsProtocolCard>
        <KdsProtocolCard
          :eyebrow="t('landing.protocols.critiqueMeta')"
          :title="t('landing.protocols.critiqueTitle')"
          :body="t('landing.protocols.critiqueBody')"
          :best-for="t('landing.protocols.critiqueBestFor')"
        >
          <template #visual>
            <KdsProtocolLoop
              kind="p02"
              :turns="3"
              :verdict-label="t('landing.protocols.critiqueVerdict')"
            />
          </template>
        </KdsProtocolCard>
        <KdsProtocolCard
          :eyebrow="t('landing.protocols.jtbdMeta')"
          :title="t('landing.protocols.jtbdTitle')"
          :body="t('landing.protocols.jtbdBody')"
          :best-for="t('landing.protocols.jtbdBestFor')"
        >
          <template #visual>
            <KdsProtocolLoop
              kind="p03"
              :turns="4"
              :verdict-label="t('landing.protocols.jtbdVerdict')"
            />
          </template>
        </KdsProtocolCard>
      </div>
    </section>

    <!-- Sample verdict -->
    <section id="verdict" class="landing__section landing__verdict">
      <header class="landing__section-header">
        <KdsEyebrow>{{ t('landing.verdict.eyebrow') }}</KdsEyebrow>
        <h2 class="landing__section-title">{{ t('landing.verdict.title') }}</h2>
      </header>
      <KdsVerdictBlock
        :eyebrow="t('landing.verdict.blockEyebrow')"
        :quote="t('landing.verdict.quote')"
        :meta="t('landing.verdict.meta')"
        :rows="verdictRows"
      />
      <div class="landing__verdict-footer">
        <p class="landing__verdict-tagline">{{ t('landing.verdict.tagline') }}</p>
        <router-link class="landing__verdict-cta" to="/verdict-demo">
          {{ t('landing.verdict.ctaInline') }}
          <KdsIcon name="arrow-right" :size="16" aria-hidden="true" />
        </router-link>
      </div>
    </section>

    <!-- Pricing -->
    <section id="pricing" class="landing__section landing__pricing landing__section--paper2">
      <header class="landing__section-header landing__section-header--center">
        <KdsEyebrow>{{ t('landing.pricing.eyebrow') }}</KdsEyebrow>
        <h2 class="landing__section-title">{{ t('landing.pricing.title') }}</h2>
        <p class="landing__section-subtitle landing__section-subtitle--center">
          {{ t('landing.pricing.subtitle') }}
        </p>
      </header>

      <LandingPricingToggleBar v-model="billingCycle" class="landing__pricing-toggle" />

      <TransitionGroup tag="div" class="landing__pricing-grid" name="pricing-stagger" appear>
        <KdsPricingCard
          key="studio"
          :tier="t('landing.pricing.studioTier')"
          :price="studioDisplayPrice"
          :suffix="t('landing.pricing.suffixMonthly')"
          :cta-label="t('landing.pricing.studioCta')"
          :cta-disabled="checkoutLoading"
          :features="studioFeatures"
          style="--i: 0"
          @cta-click="onChoosePlan('studio')"
        />
        <KdsPricingCard
          key="team"
          :tier="t('landing.pricing.teamTier')"
          :price="teamDisplayPrice"
          :suffix="t('landing.pricing.suffixMonthly')"
          :cta-label="t('landing.pricing.teamCta')"
          :cta-note="t('landing.pricing.teamCtaNote')"
          :cta-disabled="checkoutLoading"
          :features="teamFeatures"
          style="--i: 1"
          @cta-click="onChoosePlan('team')"
        />
        <KdsPricingCard
          key="custom"
          :tier="t('landing.pricing.customTier')"
          :price="t('landing.pricing.customPriceLabel')"
          :cta-label="t('landing.pricing.customCta')"
          :features="customFeatures"
          style="--i: 2"
          @cta-click="onPricingCtaClick('custom', 'landing-pricing-card')"
        >
          <template v-if="calendlyUrl" #footer>
            <KdsButton
              variant="secondary"
              icon="calendar"
              class="landing__pricing-book-call"
              :aria-label="t('landing.pricing.stickyCta.bookACallAriaLabel')"
              @click="onBookCallClick"
            >
              {{ t('landing.pricing.stickyCta.bookACall') }}
            </KdsButton>
          </template>
        </KdsPricingCard>
      </TransitionGroup>

      <!-- Error banner — shown below the pricing grid if checkout fails (CA-2) -->
      <Transition name="fade">
        <KdsBanner v-if="checkoutError" tone="danger" class="landing__pricing-checkout-error">{{
          checkoutError
        }}</KdsBanner>
      </Transition>

      <LandingPricingFaq class="landing__pricing-faq" />
    </section>

    <!-- Sticky CTA is entirely client-driven (IntersectionObserver + Teleport);
         render client-only to avoid an SSR hydration node mismatch. -->
    <KdsClientOnly>
      <LandingPricingStickyCta :billing-cycle="billingCycle" />
    </KdsClientOnly>

    <!-- Trust / What's inside (D3) -->
    <section id="trust" class="landing__section landing__trust">
      <header class="landing__section-header">
        <KdsEyebrow>{{ t('landing.trust.eyebrow') }}</KdsEyebrow>
        <h2 class="landing__section-title">{{ t('landing.trust.title') }}</h2>
      </header>
      <TransitionGroup tag="div" class="landing__trust-grid" name="trust-stagger" appear>
        <KdsFeatureCard
          key="workspaces"
          icon="layers"
          :title="t('landing.trust.workspacesTitle')"
          :body="t('landing.trust.workspacesBody')"
          :link-label="t('landing.trust.workspacesLink')"
          link-to="/about"
          style="--i: 0"
        />
        <KdsFeatureCard
          key="persona-library"
          icon="users-round"
          :title="t('landing.trust.personaLibraryTitle')"
          :body="t('landing.trust.personaLibraryBody')"
          :link-label="t('landing.trust.personaLibraryLink')"
          link-to="/docs#create-persona"
          style="--i: 1"
        />
        <KdsFeatureCard
          key="audit-export"
          icon="arrow-down-to-line"
          :title="t('landing.trust.auditExportTitle')"
          :body="t('landing.trust.auditExportBody')"
          :link-label="t('landing.trust.auditExportLink')"
          link-to="/privacy"
          style="--i: 2"
        />
        <KdsFeatureCard
          key="status-changelog"
          icon="radio"
          :title="t('landing.trust.statusChangelogTitle')"
          :body="t('landing.trust.statusChangelogBody')"
          :link-label="t('landing.trust.statusChangelogLink')"
          link-to="/changelogs"
          style="--i: 3"
        />
      </TransitionGroup>
    </section>

    <KdsFooter
      :brand="t('app.name')"
      :status-label="t('landing.footer.status')"
      :status-to="'/status'"
      :columns="footerColumns"
    />
  </q-page>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, type ComponentPublicInstance } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import KdsButton from '@/kds/primitives/KdsButton.vue';
import KdsEyebrow from '@/kds/primitives/KdsEyebrow.vue';
import KdsIcon from '@/kds/primitives/KdsIcon.vue';
import { useSeoMeta } from '@/composables/useSeoMeta';
import KdsFeatureCard from '@/kds/components/KdsFeatureCard.vue';
import KdsFooter from '@/kds/components/KdsFooter.vue';
import KdsLiveIndicator from '@/kds/components/KdsLiveIndicator.vue';
import KdsNav from '@/kds/components/KdsNav.vue';
import KdsPricingCard from '@/kds/components/KdsPricingCard.vue';
import KdsProtocolCard from '@/kds/components/KdsProtocolCard.vue';
import KdsProtocolLoop from '@/kds/components/KdsProtocolLoop.vue';
import KdsQuoteFeed from '@/kds/components/KdsQuoteFeed.vue';
import KdsRoundTable from '@/kds/components/KdsRoundTable.vue';
import KdsStepCard from '@/kds/components/KdsStepCard.vue';
import KdsVerdictBlock from '@/kds/components/KdsVerdictBlock.vue';
import { useConsent } from '@/composables/useConsent';
import { captureEvent } from '@/boot/posthog';
import { useQuoteFeed } from '@/kds/components/KdsRoundTable.parts/useQuoteFeed';
import { DEMO_PERSONAS } from '@/kds/components/KdsRoundTable.demo';
import { PRICING_DEBATE_QUOTES } from '@/kds/components/KdsRoundTable.parts/messages';
import { STUDIO_BASE_PRICE, TEAM_BASE_PRICE, type BillingCycle } from '@/config/pricing';
import { computeDisplayPrice, formatPrice } from '@/composables/usePricingDisplay';
import LandingPricingToggleBar from '@/components/landing/pricing/LandingPricingToggleBar.vue';
import LandingPricingFaq from '@/components/landing/pricing/LandingPricingFaq.vue';
import LandingPricingStickyCta from '@/components/landing/pricing/LandingPricingStickyCta.vue';
import KdsClientOnly from '@/kds/primitives/KdsClientOnly.vue';
import KdsBanner from '@/kds/primitives/KdsBanner.vue';
import { useBilling } from '@/composables/useBilling';
import { useAuthStore } from '@/stores/auth';
import { storeToRefs } from 'pinia';

const { t, tm, rt, locale } = useI18n();
const router = useRouter();
const { openPreferences } = useConsent();
const { startCheckout } = useBilling();
const authStore = useAuthStore();
const { user } = storeToRefs(authStore);

// Checkout state for studio/team direct CTAs
const checkoutLoading = ref(false);
const checkoutError = ref<string | null>(null);

// SSR-rendered meta tags via Quasar Meta plugin
useSeoMeta('landing');

// Track client-side mount to conditionally render browser-only components
const isClientMounted = ref(false);

// Billing cycle toggle state — monthly by default
const billingCycle = ref<BillingCycle>('monthly');

// Calendly URL from env (optional, hides book-a-call button when absent)
const calendlyUrl = import.meta.env.VITE_CALENDLY_URL as string | undefined;

// Prices reactive to billing cycle
const studioDisplayPrice = computed<string>(() => {
  const price = computeDisplayPrice(STUDIO_BASE_PRICE, billingCycle.value);
  return formatPrice(price, locale.value === 'fr' ? 'fr-FR' : 'en-US');
});

const teamDisplayPrice = computed<string>(() => {
  const price = computeDisplayPrice(TEAM_BASE_PRICE, billingCycle.value);
  return formatPrice(price, locale.value === 'fr' ? 'fr-FR' : 'en-US');
});

const { feedCards, pushSpeaker } = useQuoteFeed(DEMO_PERSONAS, PRICING_DEBATE_QUOTES);

// Template ref for KdsRoundTable — used to mirror its internal paused state to KdsQuoteFeed.
const roundTableRef = ref<(ComponentPublicInstance & { paused: boolean }) | null>(null);
const roundTablePaused = computed<boolean>(() => roundTableRef.value?.paused ?? false);

const navLinks = computed(() => [
  { label: t('landing.nav.product'), href: '#how' },
  { label: t('landing.nav.protocols'), href: '#protocols' },
  { label: t('landing.nav.pricing'), href: '#pricing' },
  { label: t('landing.nav.docs'), href: '/docs' },
  { label: t('landing.nav.changelog'), href: '/changelogs' }
]);

const verdictRows = computed(() => [
  { id: 'r-1', tone: 'insight' as const, text: t('landing.verdict.row1') },
  { id: 'r-2', tone: 'friction' as const, text: t('landing.verdict.row2') },
  { id: 'r-3', tone: 'witness' as const, text: t('landing.verdict.row3') }
]);

// Pricing features using tm()+rt() to handle runtimeOnly AST nodes
const studioFeatures = computed<string[]>(() =>
  (tm('landing.pricing.studioFeatures') as unknown[]).map((node) => rt(node))
);
const teamFeatures = computed<string[]>(() =>
  (tm('landing.pricing.teamFeatures') as unknown[]).map((node) => rt(node))
);
const customFeatures = computed<string[]>(() =>
  (tm('landing.pricing.customFeatures') as unknown[]).map((node) => rt(node))
);

const footerColumns = computed(() => [
  {
    title: t('landing.footer.productTitle'),
    links: [
      { label: t('landing.footer.linkOverview'), href: '#how' },
      { label: t('landing.footer.linkProtocols'), href: '#protocols' },
      { label: t('landing.footer.linkPricing'), href: '#pricing' }
    ]
  },
  {
    title: t('landing.footer.resourcesTitle'),
    links: [
      { label: t('landing.footer.linkDocs'), to: '/docs' },
      { label: t('landing.footer.linkChangelog'), to: '/changelogs' },
      { label: t('landing.footer.linkStatus'), to: '/status' }
    ]
  },
  {
    title: t('landing.footer.companyTitle'),
    links: [
      { label: t('landing.footer.linkAbout'), to: '/about' },
      {
        label: t('landing.footer.linkContact'),
        to: { path: '/about', hash: '#contact' }
      }
    ]
  },
  {
    title: t('landing.footer.legalTitle'),
    links: [
      { label: t('landing.footer.linkPrivacy'), to: '/privacy' },
      { label: t('landing.footer.linkTerms'), to: '/terms' },
      { label: t('landing.footer.linkCookies'), to: '/cookies' },
      { label: t('landing.footer.linkLegalNotice'), to: '/legal-notice' },
      { label: t('landing.footer.manageCookies'), action: openPreferences }
    ]
  }
]);

function goAuth() {
  router.push({ name: 'auth', query: { mode: 'sign-up' } });
}

/**
 * Handles a pricing CTA click for the custom tier — navigates to /configure-plan (CA-6).
 * Studio/Team CTAs use onChoosePlan instead.
 */
const onPricingCtaClick = (
  planId: 'custom',
  location: 'landing-pricing-card' = 'landing-pricing-card'
): void => {
  captureEvent('upgrade_clicked', {
    workspace_id: null,
    plan_current: 'visitor',
    plan_id: planId,
    location,
    billing_cycle: billingCycle.value
  });
  void router.push('/configure-plan');
};

/**
 * Handles a direct checkout CTA click for Studio or Team plans (CA-1, CA-2, CA-5).
 * If not authenticated → redirect to sign-up with resume query params (Parcours A).
 * If authenticated → trigger startCheckout directly (Parcours B).
 */
const onChoosePlan = async (tier: 'studio' | 'team'): Promise<void> => {
  checkoutError.value = null;

  // Capture upgrade_clicked before any redirect or checkout (CA-5).
  captureEvent('upgrade_clicked', {
    workspace_id: null,
    plan_current: 'visitor',
    plan_id: tier,
    location: 'landing-pricing-card',
    billing_cycle: billingCycle.value
  });

  // Parcours A: not authenticated → redirect to sign-up with resume params.
  if (!user.value) {
    const redirect = `/configure-plan?tier=${tier}&cycle=${billingCycle.value}`;
    void router.push({ name: 'auth', query: { mode: 'sign-up', redirect } });
    return;
  }

  // Parcours B: authenticated → direct checkout.
  checkoutLoading.value = true;
  try {
    await startCheckout(tier, billingCycle.value);
  } catch (err) {
    checkoutError.value = err instanceof Error ? err.message : t('billing.toast.checkoutError');
  } finally {
    checkoutLoading.value = false;
  }
};

/**
 * Opens Calendly in a new tab for the custom plan "book a call" CTA.
 */
const onBookCallClick = (): void => {
  captureEvent('upgrade_clicked', {
    plan_id: 'custom',
    location: 'landing-pricing-card',
    cta: 'book-a-call'
  });
  window.open(calendlyUrl, '_blank', 'noopener');
};

function goSignIn() {
  router.push({ name: 'auth', query: { mode: 'sign-in' } });
}

function goDocs() {
  router.push('/docs');
}

onMounted(() => {
  isClientMounted.value = true;

  if (typeof document !== 'undefined') {
    // Keep document.title in sync for client-side navigation (CA-1, CA-2 test compat).
    document.title = 'Korum — Synthetic user research panels that actually debate';
    // Sync html[lang] with the active i18n locale (CA-6).
    document.documentElement.lang = locale.value;
  }
  // Push the initial card silently (no kds-settle animation, per H5).
  pushSpeaker(0, { isInitial: true });
});
</script>

<style scoped lang="scss">
.landing {
  background: var(--paper);
  color: var(--ink);

  &__hero {
    position: relative;
    overflow: hidden;
    padding: var(--space-11) var(--space-7);
    max-width: 1280px;
    margin: 0 auto;

    // Themed background crossfade via pseudo-elements
  }

  &__hero-grid {
    position: relative;
    z-index: 1;
    display: grid;
    grid-template-columns: 1fr;
    gap: var(--space-9);
    align-items: center;

    @media (min-width: 960px) {
      grid-template-columns: 1.1fr 0.9fr;
    }
  }

  &__hero-text {
    display: flex;
    flex-direction: column;
    gap: var(--space-5);
  }

  &__hero-eyebrow {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
  }

  &__hero-dot {
    width: 6px;
    height: 6px;
    background: var(--signal);
    border-radius: var(--r-pill);
  }

  &__hero-title {
    margin: 0;
    font-family: var(--font-display);
    font-size: var(--fs-display-l);
    line-height: var(--lh-display-l);
    letter-spacing: var(--ls-display-l);
    color: var(--ink);

    @media (min-width: 960px) {
      font-size: var(--fs-display-xl);
      line-height: var(--lh-display-xl);
      letter-spacing: var(--ls-display-xl);
    }
  }

  &__hero-highlight {
    box-shadow: inset 0 -4px 0 0 var(--signal);
  }

  &__hero-sub {
    margin: 0;
    font-family: var(--font-serif);
    font-size: 22px;
    line-height: 1.45;
    color: var(--stone);
    max-width: 540px;
    font-style: italic;
  }

  &__hero-ctas {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-3);
  }

  &__hero-visual {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    overflow: hidden;

    @media (max-width: 959.98px) {
      flex-direction: column;
      overflow: visible;
    }
  }

  // SSR placeholder — reserves the hero visual space to prevent CLS
  &__hero-visual-placeholder {
    width: 480px;
    height: 480px;
    max-width: 320px;
    aspect-ratio: 1 / 1;
    background: var(--paper);
    border: var(--border);
    border-radius: var(--r-pill);

    @media (min-width: 480px) {
      max-width: 480px;
    }
  }

  &__hero-live {
    position: static;
    margin-top: var(--space-4);
    max-width: 100%;

    @media (min-width: 960px) {
      position: absolute;
      top: var(--space-4);
      right: 0;
      margin-top: 0;
      max-width: unset;
    }
  }

  &__hero-quote {
    position: static;
    margin-top: var(--space-4);
    max-width: 100%;
    transform: none;

    @media (min-width: 960px) {
      position: absolute;
      top: 60px;
      right: 0;
      margin-top: 0;
      max-width: unset;
      transform: translateY(0);
    }

    // Le fondu du bas est un mask porté par KdsQuoteFeed (cf. le composant) : les
    // cards disparaissent réellement au lieu d'être recouvertes par un dégradé vers
    // --paper, qui se voyait comme une bande noire par-dessus le round table.
    // Pas de backdrop-filter ici : ce mask crée un « backdrop root », donc les cards
    // ne peuvent plus échantillonner ce qui est peint derrière elles. Le fond
    // translucide est assombri pour rester lisible au-dessus du round table.
    :deep(.kds-quote-card) {
      background: rgba(10, 10, 10, 0.72);
    }
  }

  &__section {
    padding: var(--space-11) var(--space-7);
    scroll-margin-top: calc(var(--nav-height, 56px) + var(--space-2));
  }

  &__section--paper2 {
    background: var(--paper-2);
  }

  &__section-header {
    max-width: 1280px;
    margin: 0 auto var(--space-8);
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }

  &__section-header--center {
    text-align: center;
    align-items: center;
  }

  &__section-title {
    margin: 0;
    font-family: var(--font-display);
    font-size: var(--fs-display-m);
    line-height: var(--lh-display-m);
    letter-spacing: var(--ls-display-m);
    color: var(--ink);
  }

  &__steps {
    display: grid;
    grid-template-columns: 1fr;
    gap: var(--space-7);
    max-width: 1280px;
    margin: 0 auto;

    @media (min-width: 640px) {
      grid-template-columns: repeat(2, 1fr);
    }
    @media (min-width: 960px) {
      grid-template-columns: repeat(3, 1fr);
    }
  }

  &__protocols-grid,
  &__pricing-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: var(--space-5);
    max-width: 1280px;
    margin: 0 auto;

    @media (min-width: 640px) {
      grid-template-columns: repeat(2, 1fr);
    }
    @media (min-width: 960px) {
      grid-template-columns: repeat(3, 1fr);
    }
  }

  &__pricing-checkout-error {
    margin-top: var(--space-5);
    max-width: 1280px;
    margin-left: auto;
    margin-right: auto;
  }

  &__pricing-faq {
    margin-top: var(--space-9);
    padding-top: var(--space-9);
    border-top: var(--border);
  }

  &__verdict {
    max-width: 1280px;
    margin: 0 auto;
  }

  &__verdict-footer {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--space-3);
    max-width: 880px;
    margin: var(--space-6) auto 0;
    text-align: center;
  }

  &__verdict-tagline {
    margin: 0;
    font-family: var(--font-serif);
    font-size: var(--fs-body);
    font-style: italic;
    color: var(--stone);
    line-height: 1.5;
  }

  &__verdict-cta {
    display: inline-flex;
    align-items: center;
    gap: var(--space-1);
    font-size: var(--fs-body-sm);
    font-weight: var(--fw-medium);
    color: var(--ink);
    text-decoration: none;
    transition: color var(--d-fast) var(--ease-out);

    &:hover {
      text-decoration: underline;
    }

    &:focus-visible {
      outline: none;
      box-shadow: var(--shadow-focus);
      border-radius: var(--r-2);
    }
  }

  &__section-subtitle {
    margin: 0;
    font-size: var(--fs-body);
    line-height: 1.5;
    color: var(--stone);

    &--center {
      text-align: center;
    }
  }

  &__trust {
    max-width: 100%;
  }

  &__trust-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: var(--space-5);
    max-width: 1280px;
    margin: 0 auto;

    @media (min-width: 640px) {
      grid-template-columns: repeat(2, 1fr);
    }
    @media (min-width: 960px) {
      grid-template-columns: repeat(4, 1fr);
    }
  }
}

.landing__hero {
  background-color: transparent;
  background: none;
  z-index: 2;
  position: relative;
}

// Stagger entrance animation for pricing cards (ux.md — stagger ≤ 8 items)
.pricing-stagger-enter-active {
  transition:
    opacity var(--d-base) var(--ease-out),
    transform var(--d-base) var(--ease-out);
  transition-delay: calc(var(--i, 0) * 50ms);
}

.pricing-stagger-enter-from {
  opacity: 0;
  transform: translateY(8px);
}

// Stagger entrance animation for trust section cards (ux.md — stagger ≤ 8 items)
.trust-stagger-enter-active {
  transition:
    opacity var(--d-base) var(--ease-out),
    transform var(--d-base) var(--ease-out);
  transition-delay: calc(var(--i, 0) * 50ms);
}

.trust-stagger-enter-from {
  opacity: 0;
  transform: translateY(8px);
}

#how {
  z-index: 1;
  position: relative;
  &::before {
    content: '';
    position: absolute;
    width: 100vw;
    aspect-ratio: 16 / 9;
    bottom: 0;
    left: 0;
    z-index: -1;
    background: url('/assets/landing-page-background.jpg') center/cover no-repeat;
    opacity: 0.6;
    transform-origin: bottom center;
  }
}

// Generic fade transition for error banners and conditional UI elements
.fade-enter-active,
.fade-leave-active {
  transition: opacity var(--d-base) var(--ease-out);

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
